import { useState, useRef, useCallback, useEffect } from 'react';

export type RecordingState = 'idle' | 'recording' | 'paused' | 'stopped';

export interface UseAudioRecordingOptions {
    onRecordingComplete?: (audioBlob: Blob) => void;
    onError?: (error: Error) => void;
}

export interface UseAudioRecordingReturn {
    recordingState: RecordingState;
    audioBlob: Blob | null;
    audioUrl: string | null;
    duration: number;
    startRecording: () => Promise<void>;
    stopRecording: () => void;
    pauseRecording: () => void;
    resumeRecording: () => void;
    resetRecording: () => void;
    isRecording: boolean;
    isPaused: boolean;
    isStopped: boolean;
    error: Error | null;
}

export const useAudioRecording = (
    options: UseAudioRecordingOptions = {},
): UseAudioRecordingReturn => {
    const { onRecordingComplete, onError } = options;

    const [recordingState, setRecordingState] =
        useState<RecordingState>('idle');
    const [audioBlob, setAudioBlob] = useState<Blob | null>(null);
    const [audioUrl, setAudioUrl] = useState<string | null>(null);
    const [duration, setDuration] = useState<number>(0);
    const [error, setError] = useState<Error | null>(null);

    const mediaRecorderRef = useRef<MediaRecorder | null>(null);
    const audioChunksRef = useRef<Blob[]>([]);
    const streamRef = useRef<MediaStream | null>(null);
    const durationIntervalRef = useRef<number | null>(null);
    const startTimeRef = useRef<number>(0);
    const pausedDurationRef = useRef<number>(0);
    const shouldCallCompleteRef = useRef<boolean>(false);
    const audioUrlRef = useRef<string | null>(null);

    // Monotonically increasing ID used to detect stale startRecording
    // calls (e.g. from React StrictMode double-mount).
    const startCallIdRef = useRef<number>(0);

    // Keep the URL ref in sync for cleanup purposes
    useEffect(() => {
        audioUrlRef.current = audioUrl;
    }, [audioUrl]);

    const stopDurationTimer = useCallback(() => {
        if (durationIntervalRef.current !== null) {
            clearInterval(durationIntervalRef.current);
            durationIntervalRef.current = null;
        }
    }, []);

    const startDurationTimer = useCallback(() => {
        // Clear any existing interval first
        if (durationIntervalRef.current !== null) {
            clearInterval(durationIntervalRef.current);
        }

        startTimeRef.current = Date.now() - pausedDurationRef.current;
        durationIntervalRef.current = window.setInterval(() => {
            setDuration(Date.now() - startTimeRef.current);
        }, 100);
    }, []);

    /**
     * Immediately stop all tracks on the media stream to release the
     * microphone. Safe to call multiple times or when the stream is
     * already stopped / null.
     */
    const releaseStream = useCallback(() => {
        if (streamRef.current) {
            try {
                streamRef.current.getTracks().forEach((track) => {
                    track.stop();
                });
            } catch (e) {
                console.warn('Error stopping media stream tracks:', e);
            }
            streamRef.current = null;
        }
    }, []);

    const cleanup = useCallback(() => {
        // Don't trigger completion callback during cleanup
        shouldCallCompleteRef.current = false;

        // Invalidate any in-flight startRecording calls so that if a
        // getUserMedia promise resolves after cleanup, the stream is
        // discarded instead of being stored.
        startCallIdRef.current++;

        // Stop and clean up MediaRecorder
        if (mediaRecorderRef.current) {
            try {
                // Remove event listeners to prevent memory leaks
                mediaRecorderRef.current.ondataavailable = null;
                mediaRecorderRef.current.onstop = null;

                if (mediaRecorderRef.current.state !== 'inactive') {
                    mediaRecorderRef.current.stop();
                }
            } catch (e) {
                console.warn('Error stopping MediaRecorder:', e);
            }
            mediaRecorderRef.current = null;
        }

        // Stop the timer
        stopDurationTimer();

        // Release the microphone
        releaseStream();

        // Clean up the audio URL (read from ref so cleanup doesn't
        // depend on audioUrl state, which would cause the unmount
        // effect to re-fire on every URL change)
        if (audioUrlRef.current) {
            try {
                URL.revokeObjectURL(audioUrlRef.current);
            } catch (e) {
                console.warn('Error revoking object URL:', e);
            }
            audioUrlRef.current = null;
        }
    }, [stopDurationTimer, releaseStream]);

    const startRecording = useCallback(async () => {
        // Each call gets a unique ID. After the async getUserMedia
        // resolves we check whether this call is still the "active"
        // one. If a newer call (or cleanup) has bumped the counter,
        // we immediately stop the returned stream and bail out.
        const callId = ++startCallIdRef.current;

        try {
            setError(null);

            // Release any lingering stream from a previous recording
            releaseStream();

            const stream = await navigator.mediaDevices.getUserMedia({
                audio: true,
            });

            // Stale check: another startRecording (or cleanup) happened
            // while we were waiting for the mic. Stop this stream
            // immediately so we don't leak it.
            if (callId !== startCallIdRef.current) {
                stream.getTracks().forEach((track) => track.stop());
                return;
            }

            streamRef.current = stream;

            const mediaRecorder = new MediaRecorder(stream);
            mediaRecorderRef.current = mediaRecorder;
            audioChunksRef.current = [];

            mediaRecorder.ondataavailable = (event) => {
                if (event.data.size > 0) {
                    audioChunksRef.current.push(event.data);
                }
            };

            mediaRecorder.onstop = () => {
                const blob = new Blob(audioChunksRef.current, {
                    type: 'audio/webm',
                });
                const url = URL.createObjectURL(blob);

                setAudioBlob(blob);
                setAudioUrl(url);
                setRecordingState('stopped');
                stopDurationTimer();

                // Belt-and-suspenders: release stream here too in case
                // stopRecording's release was somehow skipped
                releaseStream();

                // Only call completion callback if this was an intentional stop
                if (shouldCallCompleteRef.current) {
                    onRecordingComplete?.(blob);
                    shouldCallCompleteRef.current = false; // Reset flag
                }
            };

            mediaRecorder.start();
            setRecordingState('recording');
            startDurationTimer();
        } catch (err) {
            // If this call was superseded, don't touch state
            if (callId !== startCallIdRef.current) return;

            // If getUserMedia succeeded but something else threw,
            // make sure we release the stream
            releaseStream();

            const error =
                err instanceof Error
                    ? err
                    : new Error('Failed to start recording');
            setError(error);
            setRecordingState('idle');
            onError?.(error);
        }
    }, [
        onRecordingComplete,
        onError,
        startDurationTimer,
        stopDurationTimer,
        releaseStream,
    ]);

    const stopRecording = useCallback(() => {
        if (
            mediaRecorderRef.current &&
            mediaRecorderRef.current.state !== 'inactive'
        ) {
            shouldCallCompleteRef.current = true; // Mark as intentional completion
            mediaRecorderRef.current.stop();

            // Release the microphone immediately — don't wait for the
            // async onstop event. MediaRecorder.stop() gathers remaining
            // data synchronously; the stream is no longer needed.
            releaseStream();
        }
    }, [releaseStream]);

    const pauseRecording = useCallback(() => {
        if (
            mediaRecorderRef.current &&
            mediaRecorderRef.current.state === 'recording'
        ) {
            mediaRecorderRef.current.pause();
            setRecordingState('paused');
            // Calculate the exact duration at pause time
            pausedDurationRef.current = Date.now() - startTimeRef.current;
            setDuration(pausedDurationRef.current);
            stopDurationTimer();
        }
    }, [stopDurationTimer]);

    const resumeRecording = useCallback(() => {
        if (
            mediaRecorderRef.current &&
            mediaRecorderRef.current.state === 'paused'
        ) {
            mediaRecorderRef.current.resume();
            setRecordingState('recording');
            startDurationTimer();
        }
    }, [startDurationTimer]);

    const resetRecording = useCallback(() => {
        shouldCallCompleteRef.current = false; // Don't trigger completion

        if (mediaRecorderRef.current) {
            if (mediaRecorderRef.current.state !== 'inactive') {
                mediaRecorderRef.current.stop();
            }
            mediaRecorderRef.current = null;
        }

        cleanup();
        setRecordingState('idle');
        setAudioBlob(null);
        setAudioUrl(null);
        setDuration(0);
        setError(null);
        audioChunksRef.current = [];
        pausedDurationRef.current = 0;
    }, [cleanup]);

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            cleanup();
        };
    }, [cleanup]);

    return {
        recordingState,
        audioBlob,
        audioUrl,
        duration,
        startRecording,
        stopRecording,
        pauseRecording,
        resumeRecording,
        resetRecording,
        isRecording: recordingState === 'recording',
        isPaused: recordingState === 'paused',
        isStopped: recordingState === 'stopped',
        error,
    };
};
